import type { AxiosResponse } from "axios";

import type { ApiError, CustomResponse } from "./ApiClient";

interface ApiHttpErrorInit {
  status: number;
  url?: string;
  errors?: ApiError[] | null;
  correlationId?: string;
  clientUniqueId?: string;
  response?: AxiosResponse;
}

export class ApiBusinessError extends Error {
  public readonly status: number;
  public readonly url?: string;
  public readonly errors: ApiError[];
  public readonly data: unknown;
  public readonly actualResponseData: unknown;
  public readonly correlationId?: string;

  constructor(response: CustomResponse<unknown>) {
    const errors = response.errors ?? [];
    const [ firstError ] = errors;

    super(firstError?.message || firstError?.title || "Business exception");
    this.name = "ApiBusinessError";
    this.status = response.status;
    this.url = response.config?.url;
    this.errors = errors;
    this.data = response.data;
    this.actualResponseData = response.actualResponseData;
    this.correlationId = response.headers?.correlationid as string | undefined;
  }

  public get firstError(): ApiError | undefined {
    return this.errors[0];
  }

  public hasCode(code: string): boolean {
    return this.errors.some(
      (error) => error?.code?.name === code || error?.code?.value === code
    );
  }
}

export class ApiHttpError extends Error {
  public readonly status: number;
  public readonly url?: string;
  public readonly errors: ApiError[];
  public readonly correlationId?: string;
  public readonly clientUniqueId?: string;
  public readonly response?: AxiosResponse;

  constructor({
    status,
    url,
    errors,
    correlationId,
    clientUniqueId,
    response
  }: ApiHttpErrorInit) {
    super(
      `${url}, correlationId: ${correlationId}, clientUniqueId: ${clientUniqueId}`
    );
    this.name = "ApiHttpError";
    this.status = status;
    this.url = url;
    this.errors = errors ?? [];
    this.correlationId = correlationId;
    this.clientUniqueId = clientUniqueId;
    this.response = response;
  }
}

export const isBusinessError = (error: unknown): error is ApiBusinessError =>
  error instanceof ApiBusinessError;

export const isApiHttpError = (error: unknown): error is ApiHttpError =>
  error instanceof ApiHttpError;
